import type { TextStyle } from "../engines/coreComponents/types";
import {
  Align,
  FlexDirection,
  Justify
} from "../engines/layoutEngine/yoga.types";
import type { NodeStyle } from "../engines/layoutEngine/layout.types";
import {
  DARK_PALETTE,
  LIGHT_PALETTE,
  PALETTE,
  THEME
} from "./colorPalette.constants";

const STEP_ROW_GAP = 12;
const STEP_COLUMN_GAP = 20;

const SWIMLANE_MARKER_END_SIZE = 8;
const STEP_MARKER_END_SIZE = 5;
const SWIMLANE_TURNING_RADIUS = 8;
const STEP_TURNING_RADIUS = 4;

// Process Styles
const processContainer: Partial<NodeStyle> = {
  paddingTop: 0,
  paddingLeft: 0,
  flexDirection: FlexDirection.Row
};

// Object Styles
const linkedObjectNodeContainerBase: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  paddingLeft: 8,
  paddingRight: 8,
  paddingTop: 4,
  paddingBottom: 4,
  marginTop: 4,
  marginBottom: 2
};

const linkedObjectNodeBodyBase: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Column,
  alignItems: Align.Center,
  alignSelf: Align.Center
};

const linkedObjectNodeContainerLight: Partial<NodeStyle> = {
  ...linkedObjectNodeContainerBase,
  backgroundColor: LIGHT_PALETTE.FIELD_BACKGROUND
};

const linkedObjectNodeContainerDark: Partial<NodeStyle> = {
  ...linkedObjectNodeContainerBase,
  backgroundColor: DARK_PALETTE.FIELD_BACKGROUND
};

const linkedObjectNodeBodyLight: Partial<NodeStyle> = {
  ...linkedObjectNodeBodyBase,
  backgroundColor: LIGHT_PALETTE.FIELD_BACKGROUND
};

const linkedObjectNodeBodyDark: Partial<NodeStyle> = {
  ...linkedObjectNodeBodyBase,
  backgroundColor: DARK_PALETTE.FIELD_BACKGROUND
};

const objectVariableNameLight: Partial<TextStyle> = {
  fontSize: 12,
  fontWeight: "600",
  maxWidth: 190,
  color: LIGHT_PALETTE.LINKED_OBJECT_VARIABLE_NAME
};

const objectNameLight: Partial<TextStyle> = {
  fontSize: 12,
  fontWeight: "400",
  maxWidth: 190,
  color: LIGHT_PALETTE.LINKED_OBJECT_NAME
};

const objectVariableNameDark: Partial<TextStyle> = {
  fontSize: 12,
  fontWeight: "600",
  maxWidth: 190,
  color: DARK_PALETTE.LINKED_OBJECT_VARIABLE_NAME
};

const objectNameDark: Partial<TextStyle> = {
  fontSize: 12,
  fontWeight: "400",
  maxWidth: 190,
  color: DARK_PALETTE.LINKED_OBJECT_NAME
};

// Swimlane Styles

const swimlaneHeaderLeft: Partial<NodeStyle> = {
  flexGrow: 1,
  flexDirection: FlexDirection.Row
};

const swimlaneCollapseTriggerContainer: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  alignSelf: Align.Center,
  flexShrink: 0,
  cursor: "pointer"
};

const baseSwimlaneContainer: Partial<NodeStyle> = {
  width: 360,
  borderRadius: 8,
  flexDirection: FlexDirection.Column,
  marginBottom: 48, // TODO this should be driven by the gapColumn/gapRow
  marginLeft: 14,
  marginRight: 10,
  alignSelf: Align.FlexStart
};

const swimlaneContainerLight: Partial<NodeStyle> = {
  ...baseSwimlaneContainer,
  backgroundColor: LIGHT_PALETTE.SWIMLANE_BACKGROUND
};

const swimlaneFlowContainer: Partial<NodeStyle> = {
  ...baseSwimlaneContainer,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  paddingLeft: 12,
  paddingRight: 12,
  paddingTop: 12,
  paddingBottom: 12,
  gapRow: 20,
  borderWidth: 1 //#PREVENT_LAYOUT_SHIFT_ON_SELECTED_STATE: prevent layout shift when border is added on selected state
};

const swimlaneFlowContainerLight: Partial<NodeStyle> = {
  ...swimlaneFlowContainer,
  backgroundColor: LIGHT_PALETTE.SWIMLANE_BACKGROUND,
  borderColor: LIGHT_PALETTE.SWIMLANE_BACKGROUND // #PREVENT_LAYOUT_SHIFT_ON_SELECTED_STATE: keep the same color as background
};

const swimlaneContainerSelectedLight: Partial<NodeStyle> = {
  ...swimlaneContainerLight,
  borderColor: LIGHT_PALETTE.SWIMLANE_SELECTED_BORDER_COLOR,
  borderWidth: 1
};

const swimlaneFlowContainerSelectedLight: Partial<NodeStyle> = {
  ...swimlaneFlowContainerLight,
  borderColor: LIGHT_PALETTE.SWIMLANE_SELECTED_BORDER_COLOR,
  borderWidth: 1
};

const swimlaneContainerDark: Partial<NodeStyle> = {
  ...baseSwimlaneContainer,
  backgroundColor: DARK_PALETTE.SWIMLANE_BACKGROUND
};

const swimlaneFlowContainerDark: Partial<NodeStyle> = {
  ...swimlaneFlowContainer,
  backgroundColor: DARK_PALETTE.SWIMLANE_BACKGROUND,
  borderColor: DARK_PALETTE.SWIMLANE_BACKGROUND // #PREVENT_LAYOUT_SHIFT_ON_SELECTED_STATE: keep the same color as background
};

const swimlaneContainerSelectedDark: Partial<NodeStyle> = {
  ...swimlaneContainerDark,
  borderColor: DARK_PALETTE.SWIMLANE_SELECTED_BORDER_COLOR,
  borderWidth: 1
};

const swimlaneFlowContainerSelectedDark: Partial<NodeStyle> = {
  ...swimlaneFlowContainerDark,
  borderColor: DARK_PALETTE.SWIMLANE_SELECTED_BORDER_COLOR,
  borderWidth: 1
};

const swimlaneBody: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Column,
  borderRadius: 0,
  paddingLeft: 16,
  paddingRight: 16
};

const swimlaneBodyFlow: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  gapColumn: STEP_COLUMN_GAP, // this controls the gap between the columns containing the steps
  borderRadius: 0,
  flexGrow: 1,
  flexShrink: 1
};

const swimlaneBodyHidden: Partial<NodeStyle> = {
  ...swimlaneBody,
  visible: false
};

const swimlaneBodyReadonly: Partial<NodeStyle> = {
  ...swimlaneBody,
  paddingBottom: 12
};
const swimlaneBodyFlowReadonly: Partial<NodeStyle> = {
  ...swimlaneBodyFlow,
  paddingBottom: 12
};
const objectContainerReadonlyLight: Partial<NodeStyle> = {
  ...swimlaneContainerLight,
  paddingBottom: 12
};

const objectContainerReadonlySelectedLight: Partial<NodeStyle> = {
  ...swimlaneContainerSelectedLight,
  paddingBottom: 12
};

const objectContainerReadonlyDark: Partial<NodeStyle> = {
  ...swimlaneContainerDark,
  paddingBottom: 12
};

const objectContainerReadonlySelectedDark: Partial<NodeStyle> = {
  ...swimlaneContainerSelectedDark,
  paddingBottom: 12
};

const relationshipNodeContainerLight: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  justifyContent: Justify.SpaceBetween
};

const relationshipNodeContainerDark: Partial<NodeStyle> = {
  ...relationshipNodeContainerLight,
  backgroundColor: DARK_PALETTE.FIELD_BACKGROUND
};

const relationshipNodeReverseLabelLight: Partial<NodeStyle> = {
  justifyContent: Justify.Center
};

const relationshipNodeReverseLabelDark: Partial<NodeStyle> = {
  ...relationshipNodeReverseLabelLight,
  backgroundColor: DARK_PALETTE.FIELD_BACKGROUND
};

const baseSwimlaneTitleNormal: Partial<TextStyle> = {
  fontSize: 16,
  fontWeight: "600",
  maxWidth: 220
};

const baseSwimlaneTitleItalic: Partial<TextStyle> = {
  italic: true,
  ...baseSwimlaneTitleNormal
};

const swimlaneTitleNormalDark: Partial<TextStyle> = {
  ...baseSwimlaneTitleNormal,
  color: DARK_PALETTE.SWIMLANE_TITLE
};

const swimlaneTitleItalicDark: Partial<TextStyle> = {
  ...baseSwimlaneTitleItalic,
  italic: true,
  color: DARK_PALETTE.SWIMLANE_TITLE
};

const swimlaneTitleNormalLight: Partial<TextStyle> = {
  ...baseSwimlaneTitleNormal,
  color: LIGHT_PALETTE.SWIMLANE_TITLE
};

const swimlaneTitleItalicLight: Partial<TextStyle> = {
  ...baseSwimlaneTitleItalic,
  italic: true,
  color: LIGHT_PALETTE.SWIMLANE_TITLE
};

const swimlaneTitleContainer: Partial<NodeStyle> = {
  marginLeft: 8
};

const swimlaneHeader: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  paddingTop: 16,
  paddingBottom: 16,
  paddingLeft: 16,
  paddingRight: 16,
  cursor: "pointer"
};

const swimlaneFlowHeader: Partial<NodeStyle> = {
  ...swimlaneHeader,
  flexDirection: FlexDirection.Row,
  justifyContent: Justify.SpaceBetween,
  alignItems: Align.Center,
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: 0,
  paddingBottom: 0
};

// Step Styles

const stepHeaderContainer: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  paddingLeft: 20,
  paddingRight: 20,
  paddingTop: 8,
  paddingBottom: 8,
  // borderWidth: 1,
  cursor: "pointer"
};

const stepFlowHeaderContainer: Partial<NodeStyle> = {
  ...stepHeaderContainer,
  flexDirection: FlexDirection.Row,
  justifyContent: Justify.SpaceBetween,
  alignItems: Align.Center,
  paddingTop: 4,
  paddingBottom: 4,
  paddingLeft: 4,
  paddingRight: 4
};

const stepHeaderLeft: Partial<NodeStyle> = {
  paddingTop: 4,
  flexGrow: 1,
  paddingBottom: 4,
  flexDirection: FlexDirection.Row
};

const stepFlowHeaderLeft: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  alignItems: Align.Center,
  justifyContent: Justify.FlexStart,
  gapColumn: 8
};

const baseStepTitleNormal: Partial<TextStyle> = {
  fontSize: 12,
  fontWeight: "600",
  maxWidth: 150
};

const stepTitleNormalLight: Partial<TextStyle> = {
  ...baseStepTitleNormal,
  color: LIGHT_PALETTE.STEP_TITLE
};

const stepTitleNormalDark: Partial<TextStyle> = {
  ...baseStepTitleNormal,
  color: DARK_PALETTE.STEP_TITLE
};

const baseStepTitleItalic: Partial<TextStyle> = {
  ...baseStepTitleNormal,
  italic: true
};

const stepTitleItalicLight: Partial<TextStyle> = {
  ...baseStepTitleItalic,
  color: LIGHT_PALETTE.STEP_TITLE
};

const stepTitleItalicDark: Partial<TextStyle> = {
  ...baseStepTitleItalic,
  color: DARK_PALETTE.STEP_TITLE
};

const stepTitleContainer: Partial<NodeStyle> = {
  paddingLeft: 4,
  paddingRight: 4,
  paddingTop: 4,
  paddingBottom: 4,
  marginLeft: 8
};

const stepFlowTitleContainer: Partial<NodeStyle> = {
  marginTop: 4,
  marginBottom: 4
};

const stepFlowTitleContainerOnlyText: Partial<NodeStyle> = {
  ...stepFlowTitleContainer,
  marginLeft: 8,
  marginRight: 8
};

const stepFlowTitleDefaultIconContainer: Partial<NodeStyle> = {
  ...stepFlowTitleContainer,
  marginLeft: 8
};

const stepCollapseTriggerContainer = swimlaneCollapseTriggerContainer;

const stepBody: Partial<NodeStyle> = {
  borderRadius: 0,
  paddingLeft: 16,
  paddingRight: 16
  // borderWidth: 1
};

const stepBodyReadonly: Partial<NodeStyle> = {
  ...stepBody,
  paddingBottom: 12
};

const stepBodyHidden: Partial<NodeStyle> = {
  ...stepBody,
  visible: false
};

const baseStepContainer: Partial<NodeStyle> = {
  borderRadius: 8,
  marginTop: 8, //TODO: this should be driven by the gapRow/gapColumn
  marginBottom: 4 //TODO: this should be driven by the gapRow/gapColumn
};

const stepContainerLight: Partial<NodeStyle> = {
  ...baseStepContainer,
  backgroundColor: LIGHT_PALETTE.STEP_BACKGROUND,
  borderColor: LIGHT_PALETTE.STEP_BORDER_COLOR
};

const stepFlowContainerLight: Partial<NodeStyle> = {
  ...baseStepContainer,
  backgroundColor: LIGHT_PALETTE.STEP_FLOW_BACKGROUND,
  borderColor: LIGHT_PALETTE.STEP_BORDER_COLOR,
  flexGrow: 1,
  flexShrink: 1,
  marginTop: 0,
  marginBottom: 0
};

const stepContainerSelectedLight: Partial<NodeStyle> = {
  ...stepContainerLight,
  borderColor: LIGHT_PALETTE.STEP_SELECTED_BORDER_COLOR,
  borderWidth: 1
};

const stepContainerSelectedFlowLight: Partial<NodeStyle> = {
  ...stepContainerLight,
  backgroundColor: LIGHT_PALETTE.STEP_FLOW_SELECTED_BACKGROUND,
  borderWidth: 0
};

const stepContainerDark: Partial<NodeStyle> = {
  ...baseStepContainer,
  backgroundColor: DARK_PALETTE.STEP_BACKGROUND
};

const stepContainerSelectedDark: Partial<NodeStyle> = {
  ...stepContainerDark,
  borderColor: DARK_PALETTE.STEP_SELECTED_BORDER_COLOR,
  borderWidth: 1
};

// Field Styles

const baseFieldContainer: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  paddingLeft: 8,
  paddingRight: 8,
  paddingTop: 4,
  paddingBottom: 4,
  marginTop: 4,
  marginBottom: 2,
  borderRadius: 8,
  height: 35,
  justifyContent: Justify.SpaceBetween,
  alignItems: Align.Center,
  cursor: "pointer"
};

const fieldBody: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  alignItems: Align.Center,
  alignSelf: Align.Center
};

const fieldContainerLight: Partial<NodeStyle> = {
  ...baseFieldContainer,
  backgroundColor: LIGHT_PALETTE.FIELD_BACKGROUND,
  borderColor: LIGHT_PALETTE.FIELD_BORDER_COLOR,
  hover: {
    backgroundColor: LIGHT_PALETTE.FILED_BACKGROUND_HOVER_COLOR
  }
};

const fieldContainerSelectedLight: Partial<NodeStyle> = {
  ...fieldContainerLight,
  borderColor: LIGHT_PALETTE.FIELD_SELECTED_BORDER_COLOR,
  borderWidth: 1
};

const fieldContainerDark: Partial<NodeStyle> = {
  ...baseFieldContainer,
  backgroundColor: DARK_PALETTE.FIELD_BACKGROUND
};

const fieldContainerSelectedDark: Partial<NodeStyle> = {
  ...fieldContainerDark,
  borderColor: DARK_PALETTE.FIELD_SELECTED_BORDER_COLOR,
  borderWidth: 1
};

const baseFieldTitleNormal: Partial<TextStyle> = {
  fontSize: 12,
  fontWeight: "400",
  maxWidth: 190
};

const fieldTitleNormalLight: Partial<TextStyle> = {
  ...baseFieldTitleNormal,
  color: LIGHT_PALETTE.FIELD_TITLE
};

const fieldTitleNormalDark: Partial<TextStyle> = {
  ...baseFieldTitleNormal,
  color: DARK_PALETTE.FIELD_TITLE
};

const baseFieldTitleItalic: Partial<TextStyle> = {
  ...baseFieldTitleNormal,
  italic: true
};

const fieldTitleItalicLight: Partial<TextStyle> = {
  ...baseFieldTitleItalic,
  color: LIGHT_PALETTE.FIELD_TITLE
};

const fieldTitleItalicDark: Partial<TextStyle> = {
  ...baseFieldTitleItalic,
  color: DARK_PALETTE.FIELD_TITLE
};

const fieldTitleContainer: Partial<NodeStyle> = {
  marginLeft: 8,
  marginRight: 2
};

// Add Swimlane

const baseAddSwimlaneTitleText: Partial<TextStyle> = {
  fontSize: 14,
  fontWeight: "400",
  maxWidth: 280
};

const addSwimlaneTitleTextLight: Partial<TextStyle> = {
  ...baseAddSwimlaneTitleText,
  color: PALETTE.light.ADD_SWIMLANE_COLOR
};

const addSwimlaneTitleTextDark: Partial<TextStyle> = {
  ...baseAddSwimlaneTitleText,
  color: PALETTE.dark.ADD_SWIMLANE_COLOR
};

const addSwimlaneTitleContainer: Partial<NodeStyle> = {
  marginLeft: 8,
  paddingBottom: 3
};
const baseAddSwimlaneContainer: Partial<NodeStyle> = {
  width: 340,
  borderRadius: 8,
  justifyContent: Justify.Center,
  marginLeft: 14,
  height: 120,
  cursor: "pointer"
};

const addSwimlaneContainerLight: Partial<NodeStyle> = {
  ...baseAddSwimlaneContainer,
  backgroundColor: PALETTE.light.ADD_SWIMLANE_BG_COLOR
};

const addSwimlaneContainerDark: Partial<NodeStyle> = {
  ...baseAddSwimlaneContainer,
  borderColor: PALETTE.dark.ADD_SWIMLANE_BORDER
};

const addSwimlaneTextContainer: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  justifyContent: Justify.Center
};

// Add Step
const addStepContainer: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  alignItems: Align.Center,
  marginTop: 8,
  paddingTop: 12,
  marginLeft: 16,
  marginRight: 16,
  paddingBottom: 8,
  marginBottom: 8
};

// Add Field
const addFieldContainer: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,

  marginTop: 6,
  paddingTop: 8,
  marginLeft: 16,
  marginRight: 16,
  marginBottom: 8,
  paddingBottom: 8
};

const baseAddFieldText: Partial<TextStyle> = {
  fontSize: 12,
  fontWeight: "400",
  maxWidth: 280
};

const addFieldTextLight: Partial<TextStyle> = {
  ...baseAddFieldText,
  color: PALETTE.light.ADD_FIELD_COLOR
};

const addFieldTextDark: Partial<TextStyle> = {
  ...baseAddFieldText,
  color: PALETTE.dark.ADD_FIELD_COLOR
};

const addFieldTextContainer: Partial<NodeStyle> = {
  marginLeft: 8,
  paddingBottom: 3,
  flexDirection: FlexDirection.Row
};

const addNodeItemsWrapper: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  alignItems: Align.Center,
  justifyContent: Justify.Center,
  alignSelf: Align.Center,
  cursor: "pointer"
};

const zeroHeight: Partial<NodeStyle> = {
  height: 1
};

// Rule Indicator Styles
const baseRuleIndicatorContainer: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  justifyContent: Justify.SpaceBetween,
  paddingLeft: 8,
  paddingRight: 8,
  borderRadius: 15,
  paddingBottom: 3,
  paddingTop: 3,
  width: 50,
  marginRight: 5,
  flexShrink: 0,
  alignSelf: Align.Center,
  cursor: "pointer",
  hover: {
    backgroundColor: LIGHT_PALETTE.FILED_BACKGROUND_HOVER_COLOR
  }
};

const ruleIndicatorContainerLight: Partial<NodeStyle> = {
  ...baseRuleIndicatorContainer,
  backgroundColor: LIGHT_PALETTE.RULE_INDICATOR_CONTAINER_BG_COLOR,
  borderColor: LIGHT_PALETTE.FIELD_BORDER_COLOR
};

// temp styles will be removed after DS-4905
const ruleIndicatorContainerStepLight: Partial<NodeStyle> = {
  ...baseRuleIndicatorContainer,
  backgroundColor: LIGHT_PALETTE.RULE_INDICATOR_STEP_BG_COLOR,
  borderColor: LIGHT_PALETTE.FIELD_BORDER_COLOR
};

const flowRuleIndicatorContainerLight: Partial<NodeStyle> = {
  ...ruleIndicatorContainerStepLight,
  marginRight: 0,
  borderWidth: 1
};

const ruleIndicatorContainerDark: Partial<NodeStyle> = {
  ...baseRuleIndicatorContainer,
  backgroundColor: DARK_PALETTE.RULE_INDICATOR_CONTAINER_BG_COLOR
};

// temp styles will be removed after DS-4905
const ruleIndicatorContainerStepDark: Partial<NodeStyle> = {
  ...baseRuleIndicatorContainer,
  backgroundColor: DARK_PALETTE.RULE_INDICATOR_STEP_BG_COLOR,
  borderColor: DARK_PALETTE.FIELD_BORDER_COLOR
};

const flowRuleIndicatorContainerDark: Partial<NodeStyle> = {
  ...ruleIndicatorContainerStepDark,
  marginRight: 0,
  borderWidth: 1
};

const ruleIndicatorTitleDark: Partial<TextStyle> = {
  fontSize: 13,
  fontWeight: "400",
  maxWidth: 150,
  color: DARK_PALETTE.FIELD_TITLE
};

const ruleIndicatorTitleLight: Partial<TextStyle> = {
  fontSize: 12,
  fontWeight: "600",
  maxWidth: 150,
  color: LIGHT_PALETTE.RULE_INDICATOR_TITLE_COLOR
};

const ruleIndicatorsWrapper: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  alignItems: Align.Center,
  justifyContent: Justify.FlexEnd,
  alignSelf: Align.Center
};

// Required Indicator Styles
const requiredIndicatorLight: Partial<TextStyle> = {
  color: PALETTE.light.REQUIRED_ASTERISK_COLOR,
  fontSize: 10
};

const requiredIndicatorDark: Partial<TextStyle> = {
  color: PALETTE.dark.REQUIRED_ASTERISK_COLOR
};

const flowLineStyle = {
  width: 2
};

const FLOW_SWIMLANE_RULE_MIN_WIDTH = 40;

const flowSwimlaneRuleIndicator: Partial<NodeStyle> = {
  flexDirection: FlexDirection.Row,
  justifyContent: Justify.Center,
  alignSelf: Align.Center,
  borderRadius: 3.33,
  minWidth: FLOW_SWIMLANE_RULE_MIN_WIDTH,
  maxHeight: 40,
  paddingTop: 10,
  paddingBottom: 10,
  paddingLeft: 10,
  paddingRight: 10,
  backgroundColor: PALETTE.light.FLOW_SWIMLANE_RULE_INDICATOR_COLOR,
  cursor: "pointer"
};

const flowSwimlaneSelectedRuleIndicator: Partial<NodeStyle> = {
  ...flowSwimlaneRuleIndicator,
  backgroundColor: PALETTE.light.FLOW_SWIMLANE_SELECTED_RULE_INDICATOR_COLOR
};

const flowSwimlaneRuleIndicatorText: Partial<TextStyle> = {
  fontSize: 10,
  fontWeight: "600",
  maxWidth: 150,
  color: PALETTE.light.FLOW_SWIMLANE_RULE_INDICATOR_TEXT_COLOR
};

const flowLineStyleLight = {
  ...flowLineStyle,
  color: LIGHT_PALETTE.LINE_COLOR
};

const flowLineSelectedStyleLight = {
  ...flowLineStyleLight,
  color: LIGHT_PALETTE.LINE_SELECTED_COLOR
};

const flowLineStepStyleLight = {
  ...flowLineStyleLight,
  width: 1
};

const flowLineStyleDark = {
  ...flowLineStyle,
  color: DARK_PALETTE.LINE_COLOR
};

const flowLineSelectedStyleDark = {
  ...flowLineStyleDark,
  color: DARK_PALETTE.LINE_SELECTED_COLOR
};

const flowLineStepStyleDark = {
  ...flowLineStyleDark,
  width: 1
};

// Dark / Light modes
const lightTheme = {
  process: {
    container: processContainer
  },
  linkedObjectNode: {
    container: linkedObjectNodeContainerLight,
    containerSelected: fieldContainerSelectedLight,
    titleNormal: fieldTitleNormalLight,
    titleItalic: fieldTitleItalicLight,
    titleContainer: fieldTitleContainer,
    body: linkedObjectNodeBodyLight,
    objectVariableName: objectVariableNameLight,
    objectName: objectNameLight
  },
  relationshipNode: {
    container: relationshipNodeContainerLight,
    reverseLabelContainer: relationshipNodeReverseLabelLight
  },
  swimlane: {
    header: swimlaneHeader,
    flowHeader: swimlaneFlowHeader,
    headerLeft: swimlaneHeaderLeft,
    title: swimlaneTitleContainer,
    titleNormalText: swimlaneTitleNormalLight,
    titleItalicText: swimlaneTitleItalicLight,
    collapseTrigger: swimlaneCollapseTriggerContainer,
    body: swimlaneBody,
    bodyReadonly: swimlaneBodyReadonly,
    bodyHidden: swimlaneBodyHidden,
    container: swimlaneContainerLight,
    flowContainer: swimlaneFlowContainerLight,
    containerSelected: swimlaneContainerSelectedLight,
    flowContainerSelected: swimlaneFlowContainerSelectedLight,
    ruleIndicatorContainer: ruleIndicatorContainerLight,
    ruleIndicatorTitle: ruleIndicatorTitleLight,
    bodyFlow: swimlaneBodyFlow,
    bodyFlowReadonly: swimlaneBodyFlowReadonly,
    objectReadonlyContainer: objectContainerReadonlyLight,
    objectReadonlyContainerSelected: objectContainerReadonlySelectedLight
  },
  step: {
    headerContainer: stepHeaderContainer,
    flowHeaderContainer: stepFlowHeaderContainer,
    headerLeft: stepHeaderLeft,
    flowHeaderLeft: stepFlowHeaderLeft,
    titleNormal: stepTitleNormalLight,
    titleItalic: stepTitleItalicLight,
    titleContainer: stepTitleContainer,
    flowTitleContainer: stepFlowTitleContainer,
    flowTitleContainerOnlyText: stepFlowTitleContainerOnlyText,
    flowTitleDefaultIconContainer: stepFlowTitleDefaultIconContainer,
    collapseTrigger: stepCollapseTriggerContainer,
    body: stepBody,
    bodyHidden: stepBodyHidden,
    bodyReadonly: stepBodyReadonly,
    container: stepContainerLight,
    flowContainer: stepFlowContainerLight,
    containerSelected: stepContainerSelectedLight,
    flowContainerSelected: stepContainerSelectedFlowLight,
    ruleIndicatorContainer: ruleIndicatorContainerStepLight,
    flowRuleIndicatorContainer: flowRuleIndicatorContainerLight,
    ruleIndicatorTitle: ruleIndicatorTitleLight
  },
  field: {
    container: fieldContainerLight,
    containerSelected: fieldContainerSelectedLight,
    titleNormal: fieldTitleNormalLight,
    titleItalic: fieldTitleItalicLight,
    titleContainer: fieldTitleContainer,
    body: fieldBody,
    ruleIndicatorContainer: ruleIndicatorContainerLight,
    ruleIndicatorTitle: ruleIndicatorTitleLight,
    requiredIndicator: requiredIndicatorLight
  },

  relationshipField: {
    container: {
      ...fieldContainerLight,
      backgroundColor: LIGHT_PALETTE.STEP_BACKGROUND,
      borderColor: LIGHT_PALETTE.STEP_BORDER_COLOR
    },
    containerSelected: fieldContainerSelectedLight,
    titleNormal: fieldTitleNormalLight,
    titleItalic: fieldTitleItalicLight,
    titleContainer: fieldTitleContainer,
    body: fieldBody,
    ruleIndicatorContainer: ruleIndicatorContainerLight,
    ruleIndicatorTitle: ruleIndicatorTitleLight,
    requiredIndicator: requiredIndicatorLight
  },
  addSwimlane: {
    container: addSwimlaneContainerLight,
    textContainer: addSwimlaneTextContainer,
    titleText: addSwimlaneTitleTextLight,
    titleContainer: addSwimlaneTitleContainer
  },
  addField: {
    container: addFieldContainer,
    text: addFieldTextLight,
    textContainer: addFieldTextContainer,
    wrapper: addNodeItemsWrapper
  },
  addStep: {
    container: addStepContainer
  },
  flowLine: flowLineStyleLight,
  flowLineSelected: flowLineSelectedStyleLight,
  flowLineStep: flowLineStepStyleLight,
  noStyle: {},
  zeroHeight,
  ruleIndicatorsWrapper,
  flowSwimlaneRuleIndicator,
  flowSwimlaneRuleIndicatorText,
  flowSwimlaneSelectedRuleIndicator
};

const darkTheme = {
  process: {
    container: processContainer
  },
  linkedObjectNode: {
    container: linkedObjectNodeContainerDark,
    containerSelected: fieldContainerSelectedDark,
    titleNormal: fieldTitleNormalDark,
    titleItalic: fieldTitleItalicDark,
    titleContainer: fieldTitleContainer,
    body: linkedObjectNodeBodyDark,
    objectVariableName: objectVariableNameDark,
    objectName: objectNameDark
  },
  relationshipNode: {
    container: relationshipNodeContainerDark,
    reverseLabelContainer: relationshipNodeReverseLabelDark
  },
  swimlane: {
    header: swimlaneHeader,
    flowHeader: swimlaneFlowHeader,
    headerLeft: swimlaneHeaderLeft,
    title: swimlaneTitleContainer,
    titleNormalText: swimlaneTitleNormalDark,
    titleItalicText: swimlaneTitleItalicDark,
    collapseTrigger: swimlaneCollapseTriggerContainer,
    body: swimlaneBody,
    bodyReadonly: swimlaneBodyReadonly,
    bodyHidden: swimlaneBodyHidden,
    container: swimlaneContainerDark,
    flowContainer: swimlaneFlowContainerDark,
    containerSelected: swimlaneContainerSelectedDark,
    flowContainerSelected: swimlaneFlowContainerSelectedDark,
    ruleIndicatorContainer: ruleIndicatorContainerDark,
    ruleIndicatorTitle: ruleIndicatorTitleDark,
    bodyFlow: swimlaneBodyFlow,
    bodyFlowReadonly: swimlaneBodyFlowReadonly,
    objectReadonlyContainer: objectContainerReadonlyDark,
    objectReadonlyContainerSelected: objectContainerReadonlySelectedDark
  },
  step: {
    headerContainer: stepHeaderContainer,
    flowHeaderContainer: stepFlowHeaderContainer,
    headerLeft: stepHeaderLeft,
    flowHeaderLeft: stepFlowHeaderLeft,
    titleNormal: stepTitleNormalDark,
    titleItalic: stepTitleItalicDark,
    titleContainer: stepTitleContainer,
    flowTitleContainer: stepFlowTitleContainer,
    flowTitleContainerOnlyText: stepFlowTitleContainerOnlyText,
    flowTitleDefaultIconContainer: stepFlowTitleDefaultIconContainer,
    collapseTrigger: stepCollapseTriggerContainer,
    body: stepBody,
    bodyHidden: stepBodyHidden,
    bodyReadonly: stepBodyReadonly,
    container: stepContainerDark,
    flowContainer: stepFlowContainerLight,
    containerSelected: stepContainerSelectedDark,
    flowContainerSelected: stepContainerSelectedFlowLight,
    ruleIndicatorContainer: ruleIndicatorContainerStepDark,
    flowRuleIndicatorContainer: flowRuleIndicatorContainerDark,
    ruleIndicatorTitle: ruleIndicatorTitleDark
  },
  field: {
    container: fieldContainerDark,
    containerSelected: fieldContainerSelectedDark,
    titleNormal: fieldTitleNormalDark,
    titleItalic: fieldTitleItalicDark,
    titleContainer: fieldTitleContainer,
    body: fieldBody,
    ruleIndicatorContainer: ruleIndicatorContainerDark,
    ruleIndicatorTitle: ruleIndicatorTitleDark,
    requiredIndicator: requiredIndicatorDark
  },
  relationshipField: {
    container: {
      ...fieldContainerDark,
      borderColor: DARK_PALETTE.STEP_BACKGROUND,
      backgroundColor: DARK_PALETTE.STEP_BACKGROUND
    },
    containerSelected: fieldContainerSelectedDark,
    titleNormal: fieldTitleNormalDark,
    titleItalic: fieldTitleItalicDark,
    titleContainer: fieldTitleContainer,
    body: fieldBody,
    ruleIndicatorContainer: ruleIndicatorContainerDark,
    ruleIndicatorTitle: ruleIndicatorTitleDark,
    requiredIndicator: requiredIndicatorDark
  },
  addSwimlane: {
    container: addSwimlaneContainerDark,
    textContainer: addSwimlaneTextContainer,
    titleText: addSwimlaneTitleTextDark,
    titleContainer: addSwimlaneTitleContainer
  },
  addField: {
    container: addFieldContainer,
    text: addFieldTextDark,
    textContainer: addFieldTextContainer,
    wrapper: addNodeItemsWrapper
  },
  addStep: {
    container: addStepContainer
  },
  flowLine: flowLineStyleDark,
  flowLineSelected: flowLineSelectedStyleDark,
  flowLineStep: flowLineStepStyleDark,
  noStyle: {},
  zeroHeight,
  ruleIndicatorsWrapper,
  flowSwimlaneRuleIndicator,
  flowSwimlaneRuleIndicatorText,
  flowSwimlaneSelectedRuleIndicator
};

export const styles: Record<THEME, typeof lightTheme> = {
  [THEME.LIGHT]: lightTheme,
  [THEME.DARK]: darkTheme
};

// these are magical numbers that are used to calculate the height of the swimlane and steps
const STEP_HEIGHT =
  (lightTheme.step.headerContainer.marginTop ?? 4) +
  2 * (lightTheme.step.flowContainer.borderWidth ?? 1) +
  (lightTheme.step.flowHeaderContainer.paddingTop ?? 4) +
  (lightTheme.step.flowHeaderContainer.paddingBottom ?? 4) +
  (lightTheme.step.titleNormal.fontSize ?? 12) +
  (lightTheme.step.headerContainer.marginBottom ?? 4) +
  STEP_ROW_GAP;

const STEP_HEIGHT_READONLY = STEP_HEIGHT;
// as of now, not using readonly style for flow view
// + (lightTheme.step.bodyReadonly.paddingBottom ?? 0);

const SWIMLANE_TITLE_AND_PADDING =
  (lightTheme.swimlane.flowContainer.paddingTop ?? 12) +
  (lightTheme.swimlane.flowContainer.paddingBottom ?? 12) +
  (lightTheme.swimlane.flowContainer.gapRow ?? 24) +
  // ideally this should be max(line height, icon size)
  (lightTheme.swimlane.titleNormalText.fontSize ?? 16);

const SWIMLANE_TITLE_AND_PADDING_READONLY = SWIMLANE_TITLE_AND_PADDING;
// as of now, not using readonly style for flow view
// + (lightTheme.swimlane.bodyFlowReadonly.paddingBottom ?? 0);

const SWIMLANE_ROW_GAP = 48;
const SWIMLANE_COLUMN_GAP = 120;
const SWIMLANE_RULE_INDICATOR_GAP = 10;
const STEP_WIDTH = 252;
const MIN_SWIMLANE_WIDTH =
  STEP_WIDTH +
  (lightTheme.swimlane.flowContainer.paddingLeft ?? 12) +
  (lightTheme.swimlane.flowContainer.paddingRight ?? 12);

export const processFlowNodeConstants = {
  STEP_WIDTH,
  STEP_ROW_GAP,
  STEP_COLUMN_GAP,
  STEP_HEIGHT,
  STEP_HEIGHT_READONLY,
  SWIMLANE_TITLE_AND_PADDING,
  SWIMLANE_TITLE_AND_PADDING_READONLY,
  SWIMLANE_ROW_GAP,
  SWIMLANE_COLUMN_GAP,
  SWIMLANE_RULE_INDICATOR_GAP,
  FLOW_SWIMLANE_RULE_MIN_WIDTH,
  MIN_SWIMLANE_WIDTH
} as const;

export const lineConstants = {
  SWIMLANE_MARKER_END_SIZE,
  STEP_MARKER_END_SIZE,
  SWIMLANE_TURNING_RADIUS,
  STEP_TURNING_RADIUS
} as const;
